<script lang="ts">
  import { onMount } from "svelte";
  import { fade, scale } from "svelte/transition";
  import init, {
    WasmGame,
    get_random_value,
    find_winning_seed,
  } from "../../wasm/engine.js";
  import Card from "./Card.svelte";
  import confetti from "canvas-confetti";
  import SettingsMenu from "./SettingsMenu.svelte";
  import GiftButton from "./GiftButton.svelte";
  import UnlockReveal from "./UnlockReveal.svelte";
  import {
    CARD_BACKS,
    CARD_FACES,
    BOARD_COLORS,
    GAME_MODES,
    type UnlockableItem,
  } from "./lib/content";
  import { SoundHooks } from "./lib/soundHooks";
  import { updateMasterGain } from "./lib/soundManager";

  const victorySounds = import.meta.glob(
    "./assets/victory_sounds/*.{mp3,webm}",
    {
      query: "?url",
      import: "default",
      eager: true,
    },
  );

  const decemberVictorySounds = import.meta.glob(
    "./assets/december_victory_sounds/*.{mp3,webm}",
    {
      query: "?url",
      import: "default",
      eager: true,
    },
  );

  // Sound effects for game actions
  const soundEffects = {
    undo: import.meta.glob("./assets/sound_effects/undo/*.{mp3,webm}", {
      query: "?url",
      import: "default",
      eager: true,
    }),
    pickupCard: import.meta.glob(
      "./assets/sound_effects/pickup_card/*.{mp3,webm}",
      {
        query: "?url",
        import: "default",
        eager: true,
      },
    ),
    tapCard: import.meta.glob("./assets/sound_effects/tap_card/*.{mp3,webm}", {
      query: "?url",
      import: "default",
      eager: true,
    }),
    cardFlipped: import.meta.glob(
      "./assets/sound_effects/card_flipped/*.{mp3,webm}",
      {
        query: "?url",
        import: "default",
        eager: true,
      },
    ),
    cardMoved: import.meta.glob(
      "./assets/sound_effects/card_moved/*.{mp3,webm}",
      {
        query: "?url",
        import: "default",
        eager: true,
      },
    ),
    cardToFoundation: import.meta.glob(
      "./assets/sound_effects/card_to_foundation/*.{mp3,webm}",
      {
        query: "?url",
        import: "default",
        eager: true,
      },
    ),
    stockClicked: import.meta.glob(
      "./assets/sound_effects/stock_clicked/*.{mp3,webm}",
      {
        query: "?url",
        import: "default",
        eager: true,
      },
    ),
    shuffle: import.meta.glob("./assets/sound_effects/shuffle/*.{mp3,webm}", {
      query: "?url",
      import: "default",
      eager: true,
    }),
    pileFlipped: import.meta.glob(
      "./assets/sound_effects/pile_flipped/*.{mp3,webm}",
      {
        query: "?url",
        import: "default",
        eager: true,
      },
    ),
    invalidDrop: import.meta.glob(
      "./assets/sound_effects/invalid_drop/*.{mp3,webm}",
      {
        query: "?url",
        import: "default",
        eager: true,
      },
    ),
    invalidTap: import.meta.glob(
      "./assets/sound_effects/invalid_tap/*.{mp3,webm}",
      {
        query: "?url",
        import: "default",
        eager: true,
      },
    ),
    dealing: import.meta.glob("./assets/sound_effects/dealing/*.{mp3,webm}", {
      query: "?url",
      import: "default",
      eager: true,
    }),
    soundEnabled: import.meta.glob(
      "./assets/sound_effects/sound_enabled/*.{mp3,webm}",
      {
        query: "?url",
        import: "default",
        eager: true,
      },
    ),
  };

  // Convert glob results to sound libraries
  function buildSoundLibraries() {
    const libraries: any = {};
    for (const [name, glob] of Object.entries(soundEffects)) {
      const library: any = {};
      for (const [path, url] of Object.entries(glob)) {
        const filename = path.split("/").pop()?.split(".")[0];
        if (filename) {
          if (!library[filename]) library[filename] = {};
          if (path.endsWith(".mp3")) library[filename].mp3 = url;
          if (path.endsWith(".webm")) library[filename].webm = url;
        }
      }
      if (Object.keys(library).length > 0) {
        libraries[name] = library;
      }
    }
    return libraries;
  }

  let soundLibraries: any = {};
  export let soundHooks: SoundHooks | null = null;

  let game: WasmGame | null = null;
  let cardVisuals: {
    face_up: boolean;
    rank: number;
    suit: number;
    z_index: number;
    pile_id: number;
    position: [number, number];
  }[] = [];
  let pileVisuals: {
    pile_id: number;
    pile_type: number;
    position: [number, number];
  }[] = [];
  let pileCounts: Map<number, number> = new Map();

  let solverMetrics: {
    undos_used: number;
    game_length: number;
    avg_options_per_move: number;
    forced_moves_count: number;
  } | null = null;

  let currentCardWidth = 80;
  let currentCardHeight = 120;
  const BASE_CARD_WIDTH = 100;
  const BASE_CARD_HEIGHT = 140;
  $: cardScale = currentCardWidth / BASE_CARD_WIDTH;

  let boardMarginLeft = 0;
  let boardMarginTop = 0;
  let totalBoardWidth = 0; // Added for left-handed mode calculation

  // Settings
  let showSettings = false;
  let showUnlockReveal = false;
  let showReplaceGameModal = false;
  let unlockTargetRect: DOMRect | null = null;
  let cardFace: string = "face_standard";
  let soundEnabled = true;
  let leftHanded = false;
  let currentCardBackId: string = CARD_BACKS[0].id;
  let currentBoardColor: string = BOARD_COLORS[0].id;
  let gameMode: string = "klondike";

  // Load settings
  try {
    const savedMode = localStorage.getItem("lolo_solitaire_game_mode");
    if (
      savedMode &&
      [
        "klondike",
        "freecell",
        "spider",
        "spider_1_suit",
        "spider_2_suit",
        "yukon",
        "forty_thieves",
        "scorpion",
        "golf",
        "tripeaks",
        "pyramid",
      ].includes(savedMode)
    ) {
      gameMode = savedMode;
    }

    const savedFace = localStorage.getItem("lolo_solitaire_card_face");
    if (savedFace === "face_standard" || savedFace === "face_classic") {
      cardFace = savedFace;
    } else {
      // Migration logic
      if (savedFace === "high-contrast") cardFace = "face_standard";
      else if (savedFace === "classic") cardFace = "face_classic";
      else if (savedFace === "standard") cardFace = "face_classic"; // Old default was SVG

      // Fallback if nothing matched but we had a value (or if it was null, we keep default)
      if (savedFace && !["face_standard", "face_classic"].includes(cardFace)) {
        // If we are here, maybe it was some other legacy value or just invalid.
        // Check legacy boolean
        const oldHC = localStorage.getItem("lolo_solitaire_high_contrast");
        if (oldHC === "true") {
          cardFace = "face_standard";
        } else if (oldHC === "false") {
          cardFace = "face_classic";
        }
      }
    }

    const savedBack = localStorage.getItem("lolo_solitaire_card_back");
    if (savedBack !== null) {
      // Check if it's a number (legacy)
      if (!isNaN(parseInt(savedBack))) {
        const idx = parseInt(savedBack);
        currentCardBackId = CARD_BACKS[idx]?.id || CARD_BACKS[0].id;
      } else {
        // Validate that the saved ID still exists
        if (CARD_BACKS.some((b) => b.id === savedBack)) {
          currentCardBackId = savedBack;
        } else {
          currentCardBackId = CARD_BACKS[0].id;
        }
      }
    }

    const savedBoardColor = localStorage.getItem("lolo_solitaire_board_color");
    if (savedBoardColor && BOARD_COLORS.some((c) => c.id === savedBoardColor)) {
      currentBoardColor = savedBoardColor;
    }

    const savedSound = localStorage.getItem("lolo_solitaire_sound");
    if (savedSound !== null) soundEnabled = savedSound === "true";

    const savedHand = localStorage.getItem("lolo_solitaire_left_handed");
    if (savedHand !== null) leftHanded = savedHand === "true";
  } catch (e) {
    // Ignore
  }

  // Sync highContrastMode for backward compatibility / Card component
  // "face_standard" (new) = CSS = highContrastMode true
  $: highContrastMode = cardFace === "face_standard";

  function saveSettings() {
    try {
      localStorage.setItem("lolo_solitaire_card_face", cardFace);
      localStorage.setItem("lolo_solitaire_card_back", currentCardBackId);
      localStorage.setItem("lolo_solitaire_board_color", currentBoardColor);
      localStorage.setItem("lolo_solitaire_sound", String(soundEnabled));
      localStorage.setItem("lolo_solitaire_left_handed", String(leftHanded));
      // Update legacy key
      localStorage.setItem(
        "lolo_solitaire_high_contrast",
        String(cardFace === "face_standard"),
      );
    } catch (e) {
      // Ignore
    }
  }

  // Reactive saves
  $: {
    cardFace;
    currentCardBackId;
    currentBoardColor;
    soundEnabled;
    leftHanded;
    saveSettings();
    updateMasterGain(soundEnabled);
  }

  // Update theme color for mobile browsers and html background
  $: {
    const colorDef = BOARD_COLORS.find((c) => c.id === currentBoardColor);
    if (colorDef) {
      const metaThemeColor = document.querySelector("meta[name=theme-color]");
      if (metaThemeColor) {
        metaThemeColor.setAttribute("content", colorDef.value);
      }
      if (typeof document !== "undefined") {
        document.documentElement.style.transition = "background-color 3s ease";
        document.documentElement.style.backgroundColor = colorDef.value;
      }
    }
  }

  // React to left-handed mode change
  $: {
    leftHanded;
    if (game) updateState();
  }

  // Unlocks
  let unlockedItems: Set<string> = new Set();
  let unlockedItemDetails: Map<string, { seed: string }> = new Map();
  let pendingUnlock: string | null = null;
  let isDuplicateUnlock = false;
  let wasRerolled = false;

  $: canReroll = CARD_BACKS.some((i) => !unlockedItems.has(i.id));

  // Stats
  let lifetimeWins = 0;
  let lifetimeFoundationCards = 0;
  let isVictory = false;

  // Load unlocks and stats
  try {
    const savedUnlocks = localStorage.getItem("lolo_solitaire_unlocks");
    if (savedUnlocks) {
      unlockedItems = new Set(JSON.parse(savedUnlocks));
    } else {
      // Default unlocks
      CARD_BACKS.filter((i) => !i.locked).forEach((i) =>
        unlockedItems.add(i.id),
      );
      CARD_FACES.filter((i) => !i.locked).forEach((i) =>
        unlockedItems.add(i.id),
      );
    }

    const savedUnlockDetails = localStorage.getItem(
      "lolo_solitaire_unlock_details",
    );
    if (savedUnlockDetails) {
      unlockedItemDetails = new Map(JSON.parse(savedUnlockDetails));
    }

    const savedWins = localStorage.getItem("lolo_solitaire_lifetime_wins");
    if (savedWins) lifetimeWins = parseInt(savedWins, 10);

    const savedFoundationCards = localStorage.getItem(
      "lolo_solitaire_lifetime_foundation_cards",
    );
    if (savedFoundationCards)
      lifetimeFoundationCards = parseInt(savedFoundationCards, 10);
  } catch (e) {}

  function saveStats() {
    try {
      localStorage.setItem(
        "lolo_solitaire_lifetime_wins",
        String(lifetimeWins),
      );
      localStorage.setItem(
        "lolo_solitaire_lifetime_foundation_cards",
        String(lifetimeFoundationCards),
      );
    } catch (e) {}
  }

  function saveUnlocks() {
    try {
      localStorage.setItem(
        "lolo_solitaire_unlocks",
        JSON.stringify([...unlockedItems]),
      );
      localStorage.setItem(
        "lolo_solitaire_unlock_details",
        JSON.stringify([...unlockedItemDetails]),
      );
    } catch (e) {}
  }

  function unlockRandomItem() {
    // If we don't have a seed (legacy save?), fall back to Math.random()
    const rng1 =
      currentSeed !== null ? get_random_value(currentSeed) : Math.random();
    // Use a modified seed for the second random number to ensure it's different but deterministic
    const rng2 =
      currentSeed !== null
        ? get_random_value(currentSeed + 99999n)
        : Math.random();

    // Rarity logic: Faces are rarer.
    // Let's say 20% chance for face, 80% for back.
    let pool: UnlockableItem[] = [];
    // if (rng1 < 0.2) {
    //   pool = CARD_FACES;
    // } else {
    pool = CARD_BACKS;
    // }

    // Christmas prioritization / deprioritization
    const now = new Date();
    const month = now.getMonth();
    const date = now.getDate();
    const isDecember = month === 11;
    const isChristmasDay = isDecember && date === 25;
    const isNewYearsEve = isDecember && date === 31;
    const isNewYearsDay = month === 0 && date === 1;
    const isNewYearPeriod = isNewYearsEve || isNewYearsDay;

    const christmasKeywords = ["Christmas", "Santa"];
    const winterKeywords = ["Winter", "Snow"];
    const newYearKeywords = ["New Year", "Firework", "Party"];
    const allHolidayKeywords = [
      ...christmasKeywords,
      ...winterKeywords,
      ...newYearKeywords,
    ];

    if (isNewYearPeriod) {
      let priorityKeywords = newYearKeywords;
      const lockedPriorityItems = CARD_BACKS.filter(
        (item) =>
          !unlockedItems.has(item.id) &&
          priorityKeywords.some((keyword) => item.name.includes(keyword)),
      );

      if (lockedPriorityItems.length > 0) {
        pool = lockedPriorityItems;
      }
    } else if (isDecember) {
      // In December, prioritize Winter items
      let priorityKeywords = winterKeywords;

      // On Christmas Day specifically, prioritize Christmas items
      if (isChristmasDay) {
        priorityKeywords = christmasKeywords;
      }

      const lockedPriorityItems = CARD_BACKS.filter(
        (item) =>
          !unlockedItems.has(item.id) &&
          priorityKeywords.some((keyword) => item.name.includes(keyword)),
      );

      if (lockedPriorityItems.length > 0) {
        pool = lockedPriorityItems;
      } else if (isChristmasDay) {
        // Fallback for Christmas Day: Try Winter items if Christmas items are exhausted
        const lockedWinterItems = CARD_BACKS.filter(
          (item) =>
            !unlockedItems.has(item.id) &&
            winterKeywords.some((keyword) => item.name.includes(keyword)),
        );
        if (lockedWinterItems.length > 0) {
          pool = lockedWinterItems;
        }
      }
    } else {
      // Not December: Filter out all holiday items unless they are the only ones left
      const nonHolidayItems = pool.filter(
        (item) =>
          !allHolidayKeywords.some((keyword) => item.name.includes(keyword)),
      );

      if (nonHolidayItems.length > 0) {
        pool = nonHolidayItems;
      }
    }

    if (pool.length === 0) return;

    const item = pool[Math.floor(rng2 * pool.length)];

    isDuplicateUnlock = unlockedItems.has(item.id);
    wasRerolled = false;
    unlockedItems.add(item.id);
    if (currentSeed !== null) {
      unlockedItemDetails.set(item.id, { seed: currentSeed.toString() });
    }
    unlockedItems = unlockedItems; // Trigger reactivity
    unlockedItemDetails = unlockedItemDetails; // Trigger reactivity
    pendingUnlock = item.id;
    saveUnlocks();
  }

  function handleReroll() {
    const lockedBacks = CARD_BACKS.filter((i) => !unlockedItems.has(i.id));
    if (lockedBacks.length === 0) return;

    const item = lockedBacks[Math.floor(Math.random() * lockedBacks.length)];

    unlockedItems.add(item.id);
    unlockedItems = unlockedItems;
    pendingUnlock = item.id;
    isDuplicateUnlock = false;
    wasRerolled = true;
    saveUnlocks();
  }

  // Drag state
  let isDragging = false;
  let dragOffset = { x: 0, y: 0 };
  let initialMousePos = { x: 0, y: 0 };
  let draggedCardIds = new Set<number>();
  let dragSourcePileId: number | null = null;
  let dragTargetPileId: number | null = null;
  let primaryDraggedCardId: number | null = null;
  let animatingCardIds = new Set<number>();
  let dropTargets: {
    id: number;
    rect: DOMRect;
    type: "pile" | "card";
    pileId: number;
  }[] = [];
  let draggedCardInitialRect: DOMRect | null = null;
  let animationTimeouts = new Map<number, ReturnType<typeof setTimeout>>();
  let previousPositions = new Map<number, string>();
  let isAnimating = false;
  let isResizing = false;
  let resizeTimeout: ReturnType<typeof setTimeout>;
  let gameGeneration = 0;

  let effectiveTopCardIds = new Set<number>();
  let effectivePileHasCards = new Set<number>();
  $: {
    const tops = new Set<number>();
    const hasCards = new Set<number>();
    if (cardVisuals) {
      const pileCards = new Map<number, { id: number; z: number }[]>();
      cardVisuals.forEach((v, id) => {
        if (!v) return;
        // Ignore dragged cards for top calculation so we highlight the card underneath
        if (draggedCardIds.has(id)) return;

        if (!pileCards.has(v.pile_id)) {
          pileCards.set(v.pile_id, []);
        }
        pileCards.get(v.pile_id)!.push({ id, z: v.z_index });
      });

      for (const [pileId, cards] of pileCards) {
        if (cards.length > 0) {
          cards.sort((a, b) => b.z - a.z);
          tops.add(cards[0].id);
          hasCards.add(pileId);
        }
      }
    }
    effectiveTopCardIds = tops;
    effectivePileHasCards = hasCards;
  }

  let hintInteraction: any = null;
  let lastInteractionTime = 0;
  let urlSeed: bigint | null = null;
  let currentSeed: bigint | null = null;
  let solutionMoves: any[] = [];
  let isSolving = false;
  let validMovesCount = 0;

  const GLOBAL_ANIMATION_FACTOR = 1.0;
  const INTERACTION_COOLDOWN = 200;

  // Extract seed from URL parameters
  function getUrlSeed(): bigint | null {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search);
    const seedParam = params.get("deal");
    if (seedParam) {
      try {
        const seedNum = BigInt(seedParam);
        return seedNum;
      } catch (e) {
        console.warn("Invalid seed parameter:", seedParam);
        return null;
      }
    }
    return null;
  }

  const SUITS = ["spades", "hearts", "diamonds", "clubs"];
  const RANKS = [
    null,
    "ace",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
    "9",
    "10",
    "jack",
    "queen",
    "king",
  ];
  // Compute stable (unzoomed) viewport dimensions.
  function getStableViewport() {
    const vv: any =
      typeof window !== "undefined" ? (window as any).visualViewport : null;
    if (vv && vv.scale && vv.scale !== 0) {
      // If scale is close to 1, prefer window.innerWidth to avoid rounding issues or partial viewports
      if (Math.abs(vv.scale - 1) < 0.01) {
        return { width: window.innerWidth, height: window.innerHeight };
      }
      // visualViewport.width * scale ~= layout viewport width before pinch.
      return { width: vv.width * vv.scale, height: vv.height * vv.scale };
    }
    return { width: window.innerWidth, height: window.innerHeight };
  }

  function getCardSrc(rank: number, suit: number) {
    return `${import.meta.env.BASE_URL}card_faces/${RANKS[rank]}_of_${SUITS[suit]}.svg`;
  }

  let confettiInterval: ReturnType<typeof setInterval> | null = null;
  let audioContext: AudioContext | null = null;
  let masterGain: GainNode | null = null;

  function ensureAudioContext() {
    if (!audioContext) {
      audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      masterGain = audioContext.createGain();
      masterGain.connect(audioContext.destination);
      masterGain.gain.value = soundEnabled ? 1 : 0;
    }
    if (audioContext.state === "suspended") {
      audioContext.resume().catch(() => {});
    }
  }

  $: if (audioContext && masterGain) {
    const now = audioContext.currentTime;
    masterGain.gain.cancelScheduledValues(now);
    masterGain.gain.setValueAtTime(masterGain.gain.value, now);

    if (soundEnabled) {
      masterGain.gain.linearRampToValueAtTime(1, now + 0.1);
    } else {
      masterGain.gain.linearRampToValueAtTime(0, now + 0.3);
    }
  }

  async function playVictorySound() {
    if (!soundEnabled) return;

    // Group sounds by filename (without extension)
    const soundGroups: Record<string, { mp3?: string; webm?: string }> = {};

    const now = new Date();
    const month = now.getMonth();
    const date = now.getDate();
    const isDecember = month === 11;
    const isNewYearsEve = isDecember && date === 31;
    const isNewYearsDay = month === 0 && date === 1;
    const isNewYearPeriod = isNewYearsEve || isNewYearsDay;

    let sourceSounds = victorySounds;

    if (isNewYearPeriod) {
      const newYearSounds: any = {};
      for (const [path, url] of Object.entries(victorySounds)) {
        if (path.toLowerCase().includes("newyears")) {
          newYearSounds[path] = url;
        }
      }
      if (Object.keys(newYearSounds).length > 0) {
        sourceSounds = newYearSounds;
      } else if (isDecember && Object.keys(decemberVictorySounds).length > 0) {
        sourceSounds = decemberVictorySounds;
      }
    } else if (isDecember && Object.keys(decemberVictorySounds).length > 0) {
      sourceSounds = decemberVictorySounds;
    }

    for (const [path, url] of Object.entries(sourceSounds)) {
      const filename = path.split("/").pop()?.split(".")[0];
      if (filename) {
        if (!soundGroups[filename]) soundGroups[filename] = {};
        if (path.endsWith(".mp3")) soundGroups[filename].mp3 = url as string;
        if (path.endsWith(".webm")) soundGroups[filename].webm = url as string;
      }
    }

    const groupKeys = Object.keys(soundGroups);
    if (groupKeys.length === 0) return;

    const randomKey = groupKeys[Math.floor(Math.random() * groupKeys.length)];
    const selectedGroup = soundGroups[randomKey];

    // Check for Opus support
    const audioTest = new Audio();
    const supportsOpus =
      audioTest.canPlayType('audio/webm; codecs="opus"') === "probably" ||
      audioTest.canPlayType('audio/webm; codecs="opus"') === "maybe";

    let playUrl = selectedGroup.mp3;
    if (supportsOpus && selectedGroup.webm) {
      playUrl = selectedGroup.webm;
    }

    if (!playUrl) return;

    try {
      ensureAudioContext();

      if (audioContext!.state === "suspended") {
        await audioContext!.resume();
      }

      const response = await fetch(playUrl);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await audioContext!.decodeAudioData(arrayBuffer);

      const source = audioContext!.createBufferSource();
      source.buffer = audioBuffer;

      const gainNode = audioContext!.createGain();
      source.connect(gainNode);
      if (masterGain) {
        gainNode.connect(masterGain);
      } else {
        gainNode.connect(audioContext!.destination);
      }

      const now = audioContext!.currentTime;
      const duration = audioBuffer.duration;

      // Brief fade in to prevent popping (0.1s)
      gainNode.gain.setValueAtTime(0, now);
      gainNode.gain.linearRampToValueAtTime(1, now + 0.1);

      // Fade out at the end (last 1.0s)
      const fadeOutStart = Math.max(now + 0.1, now + duration - 1.0);
      gainNode.gain.setValueAtTime(1, fadeOutStart);
      gainNode.gain.linearRampToValueAtTime(0, now + duration);

      source.start(now);
    } catch (e) {
      console.error("Failed to play victory sound", e);
    }
  }

  function checkForVictory(events: any[]) {
    for (const event of events) {
      if (event === "Victory" && !isVictory) {
        isVictory = true;
        lifetimeWins++;
        saveStats();
        playVictorySound();
        unlockRandomItem();

        const now = new Date();
        const month = now.getMonth();
        const date = now.getDate();
        const isDecember = month === 11;
        const isNewYearsEve = isDecember && date === 31;
        const isNewYearsDay = month === 0 && date === 1;
        const isNewYearPeriod = isNewYearsEve || isNewYearsDay;

        if (isNewYearPeriod) {
          let somethingUnlocked = false;

          // Unlock midnight board
          if (!unlockedItems.has("board_midnight")) {
            unlockedItems.add("board_midnight");
            somethingUnlocked = true;
          }

          if (somethingUnlocked) {
            unlockedItems = unlockedItems; // Trigger reactivity
            saveUnlocks();
          }

          // Assign midnight board
          currentBoardColor = "board_midnight";
        } else if (isDecember) {
          let somethingUnlocked = false;

          // Unlock red board
          if (!unlockedItems.has("board_red")) {
            unlockedItems.add("board_red");
            somethingUnlocked = true;
          }

          /*
          // Unlock Winter card back
          if (!unlockedItems.has("back_img_Winter")) {
            unlockedItems.add("back_img_Winter");
            somethingUnlocked = true;
          }
          */

          if (somethingUnlocked) {
            unlockedItems = unlockedItems; // Trigger reactivity
            saveUnlocks();
          }

          // Assign red board
          currentBoardColor = "board_red";
        }

        // Initial burst
        if (isNewYearPeriod) {
          // Fireworks burst
          const duration = 3000;
          const animationEnd = Date.now() + duration;
          const defaults = {
            startVelocity: 30,
            spread: 360,
            ticks: 60,
            zIndex: 0,
          };

          const randomInRange = (min: number, max: number) =>
            Math.random() * (max - min) + min;

          const interval: any = setInterval(function () {
            const timeLeft = animationEnd - Date.now();

            if (timeLeft <= 0) {
              return clearInterval(interval);
            }

            const particleCount = 50 * (timeLeft / duration);
            // since particles fall down, start a bit higher than random
            confetti({
              ...defaults,
              particleCount,
              origin: {
                x: randomInRange(0.1, 0.3),
                y: Math.random() - 0.2,
              },
            });
            confetti({
              ...defaults,
              particleCount,
              origin: {
                x: randomInRange(0.7, 0.9),
                y: Math.random() - 0.2,
              },
            });
          }, 250);
        } else if (isDecember) {
          // Create a "pre-existing" snow effect by spawning particles at random positions
          for (let i = 0; i < 10; i++) {
            confetti({
              particleCount: 5,
              origin: { x: Math.random(), y: Math.random() * 0.5 }, // Top half of screen
              colors: ["#ffffff"],
              shapes: ["circle"],
              gravity: 0.25,
              scalar: Math.random() * 0.4 + 0.5,
              drift: (Math.random() - 0.5) * 0.8,
              startVelocity: 0,
              ticks: 300,
            });
          }
        } else {
          confetti({
            particleCount: 500,
            spread: 70,
            origin: { y: 0.6 },
          });
        }

        // Start repeating confetti
        if (confettiInterval) clearInterval(confettiInterval);

        startConfettiInterval();
      }
    }
  }

  import { onDestroy } from "svelte";

  function clearConfettiInterval() {
    if (confettiInterval) {
      clearInterval(confettiInterval);
      confettiInterval = null;
    }
    try {
      confetti.reset();
    } catch (e) {
      // ignore
    }
  }

  function startConfettiInterval() {
    if (confettiInterval) return;

    const now = new Date();
    const month = now.getMonth();
    const date = now.getDate();
    const isDecember = month === 11;
    const isNewYearsEve = isDecember && date === 31;
    const isNewYearsDay = month === 0 && date === 1;
    const isNewYearPeriod = isNewYearsEve || isNewYearsDay;

    if (isNewYearPeriod) {
      // Occasional fireworks
      confettiInterval = setInterval(() => {
        if (document.hidden) return;
        const xOrigin = Math.random();
        confetti({
          particleCount: 100,
          startVelocity: 30,
          spread: 360,
          origin: {
            x: Math.random(),
            // since they fall down, start a bit higher than random
            y: Math.random() - 0.2,
          },
        });
      }, 2000);
    } else if (isDecember) {
      // Gentle continuous snow
      confettiInterval = setInterval(() => {
        if (document.hidden) return;
        confetti({
          particleCount: 1,
          startVelocity: 0,
          ticks: 800,
          origin: {
            x: Math.random(),
            y: -0.05, // Start slightly above screen
          },
          colors: ["#ffffff"],
          shapes: ["circle"],
          gravity: 0.25,
          scalar: Math.random() * 0.4 + 0.5,
          drift: (Math.random() - 0.5) * 0.8,
        });
      }, 50); // High frequency, low particle count
    } else {
      // Standard confetti bursts
      confettiInterval = setInterval(() => {
        if (document.hidden) return;
        const xOrigin = Math.random();
        confetti({
          particleCount: 50,
          angle: xOrigin < 0.5 ? 60 : 120,
          spread: 55,
          origin: { x: xOrigin, y: 0.7 },
        });
      }, 1500);
    }
  }

  function handleVisibilityChange() {
    if (document.hidden) {
      clearConfettiInterval();
    } else {
      if (isVictory) {
        startConfettiInterval();
      }
    }
  }

  function handleKeydown(event: KeyboardEvent) {
    if (showSettings) return;
    if ((event.key === "h" || event.key === "H") && game) {
      handleHint();
    }
  }

  onMount(() => {
    window.addEventListener("keydown", handleKeydown);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Initialize sound libraries and hooks
    soundLibraries = buildSoundLibraries();
    soundHooks = new SoundHooks(soundLibraries);

    urlSeed = getUrlSeed();
    initGame();
  });

  onDestroy(() => {
    window.removeEventListener("keydown", handleKeydown);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    clearConfettiInterval();
  });

  async function initGame() {
    const savedState = localStorage.getItem("lolo_solitaire_state");
    if (savedState) {
      try {
        game = WasmGame.load_game(savedState);
        const savedSeed = localStorage.getItem("lolo_solitaire_seed");
        if (savedSeed) {
          currentSeed = BigInt(savedSeed);
        }

        // Check if we should prompt to replace
        if (
          urlSeed !== null &&
          currentSeed !== null &&
          urlSeed !== currentSeed
        ) {
          const hasMoves = game.can_undo();
          // Check if won (all 52 cards in foundation piles)
          // We need to get pile visuals to check this
          // Since we haven't called processGameEvents yet, we need to get them directly
          // Invert leftHanded because the engine default (false) is Stock Left
          const engineLeftHanded = !leftHanded;
          const piles = game.get_pile_visuals(engineLeftHanded);
          const foundationPileIds = new Set(
            piles
              .filter((p: any) => p.pile_type === 3)
              .map((p: any) => p.pile_id),
          );
          const cards = game.get_card_visuals(engineLeftHanded, 1000);
          let cardsInFoundation = 0;
          for (const card of cards) {
            if (foundationPileIds.has(card.pile_id)) {
              cardsInFoundation++;
            }
          }
          const isWon = cardsInFoundation === 52;

          if (hasMoves && !isWon) {
            showReplaceGameModal = true;
            // Render the saved game in the background
            handleResize();
            updateState();
            return;
          } else {
            // If no moves or already won, auto-replace
            await startNewGame(urlSeed);
            return;
          }
        }

        if (typeof window !== "undefined" && currentSeed !== null) {
          const url = new URL(window.location.href);
          url.searchParams.set("deal", currentSeed.toString());
          window.history.replaceState({}, "", url.toString());
        }
        handleResize();
        updateState();
        return;
      } catch (e) {
        console.error("Failed to load saved game", e);
      }
    }
    if (urlSeed !== null) {
      await startNewGame(urlSeed);
    } else {
      // Check for winning_deal param here too, although startNewGame handles it if forcedSeed is null
      // But we want to pass null explicitly if no urlSeed
      await startNewGame();
    }
  }

  async function processGameEvents(
    events: any[],
    immediateCardIds: Set<number> = new Set(),
    generation: number = gameGeneration,
  ) {
    if (generation !== gameGeneration) return;
    isAnimating = true;

    // Check if we need to resize based on new pile sizes
    performLayout();
    const { height } = getStableViewport();

    // Invert leftHanded because the engine default (false) is Stock Left,
    // but the user expects Left Handed Mode (true) to be Stock Left.
    const engineLeftHanded = !leftHanded;

    const finalCardVisuals = game!.get_card_visuals(
      engineLeftHanded,
      boardArea.height,
    );
    const finalPileVisuals = game!.get_pile_visuals(engineLeftHanded);

    pileVisuals = finalPileVisuals;

    // Update counts
    const counts = new Map<number, number>();
    let currentFoundationCount = 0;

    // Identify foundation piles (Type 3 in Klondike, Type 1 in FreeCell)
    const foundationPileIds = new Set(
      pileVisuals
        .filter((p) => p.pile_type === 3 || p.pile_type === 1)
        .map((p) => p.pile_id),
    );

    for (const card of finalCardVisuals) {
      counts.set(card.pile_id, (counts.get(card.pile_id) || 0) + 1);
      if (foundationPileIds.has(card.pile_id)) {
        currentFoundationCount++;
      }
    }
    pileCounts = counts;

    // Track foundation stats
    if (lastFoundationCount !== null) {
      const diff = currentFoundationCount - lastFoundationCount;
      if (diff !== 0) {
        lifetimeFoundationCards += diff;
        saveStats();
      }
    }
    lastFoundationCount = currentFoundationCount;

    // Immediate updates
    if (immediateCardIds.size > 0) {
      const newVisuals = [...cardVisuals];
      immediateCardIds.forEach((id) => {
        if (finalCardVisuals[id]) {
          newVisuals[id] = finalCardVisuals[id];
        }
      });
      setCardVisuals(newVisuals);
    }

    for (const event of events) {
      if (generation !== gameGeneration) return;

      if (typeof event === "object") {
        if ("CardsMoved" in event) {
          const { card_ids, animation_factor } = event.CardsMoved;
          let anyChanged = false;
          const newVisuals = [...cardVisuals];

          // Heuristic: Check if the move inverts the pile order (e.g. recycling waste to stock)
          // If the card that was on top (highest Z) ends up on bottom (lowest Z), we preserve Z-index
          // to avoid visual jumping during the animation.
          let preserveZIndex = false;
          if (card_ids.length > 1) {
            let maxCurrentZ = -1;
            let maxCurrentId = -1;
            let minTargetZ = 999999;
            let minTargetId = -1;

            for (const id of card_ids) {
              const current = cardVisuals[id];
              const target = finalCardVisuals[id];
              if (current && target) {
                if (current.z_index > maxCurrentZ) {
                  maxCurrentZ = current.z_index;
                  maxCurrentId = id;
                }
                if (target.z_index < minTargetZ) {
                  minTargetZ = target.z_index;
                  minTargetId = id;
                }
              }
            }

            if (maxCurrentId !== -1 && maxCurrentId === minTargetId) {
              preserveZIndex = true;
            }
          }

          // Update Z-index for ALL cards to their final state to ensure correct layering
          // during the animation, unless we are preserving Z-index for this move.
          // This prevents cards from going "under" others when the global Z-index shifts.
          finalCardVisuals.forEach((target, id) => {
            if (newVisuals[id] && !immediateCardIds.has(id)) {
              newVisuals[id] = {
                ...newVisuals[id],
                z_index: preserveZIndex
                  ? newVisuals[id].z_index
                  : target.z_index,
              };
            }
          });

          for (const card_id of card_ids) {
            if (!immediateCardIds.has(card_id)) {
              const targetVisual = finalCardVisuals[card_id];
              const currentVisual = cardVisuals[card_id];
              newVisuals[card_id] = {
                ...targetVisual,
                z_index:
                  preserveZIndex && currentVisual
                    ? currentVisual.z_index
                    : targetVisual.z_index,
                face_up: currentVisual
                  ? currentVisual.face_up
                  : targetVisual.face_up,
              };
              anyChanged = true;
            }
          }

          if (anyChanged) {
            // Check if any cards moved to foundation and play appropriate sound
            const foundationPileIds = new Set(
              pileVisuals
                .filter((p) => p.pile_type === 3)
                .map((p) => p.pile_id),
            );
            let movedToFoundation = false;
            let movedToOtherPile = false;
            for (const card_id of card_ids) {
              const targetVisual = finalCardVisuals[card_id];
              if (foundationPileIds.has(targetVisual.pile_id)) {
                movedToFoundation = true;
                if (soundHooks) {
                  const pileSize = pileCounts.get(targetVisual.pile_id) || 0;
                  soundHooks.cardToFoundation(pileSize + 1, true);
                }
              } else {
                movedToOtherPile = true;
              }
            }

            // Play general card moved sound for non-foundation moves
            if (movedToOtherPile && soundHooks) {
              soundHooks.cardMoved();
            }

            setCardVisuals(newVisuals);
            await new Promise((r) =>
              setTimeout(r, 50 * GLOBAL_ANIMATION_FACTOR * animation_factor),
            );
          }
        } else if ("CardFlipped" in event) {
          const { card_id } = event.CardFlipped;
          if (!immediateCardIds.has(card_id)) {
            const newVisuals = [...cardVisuals];
            newVisuals[card_id] = finalCardVisuals[card_id];
            setCardVisuals(newVisuals);

            // Play card flipped sound
            if (soundHooks) {
              soundHooks.cardFlipped();
            }

            await new Promise((r) =>
              setTimeout(r, 100 * GLOBAL_ANIMATION_FACTOR),
            );
          }
        } else if ("PileFlipped" in event) {
          const { pile_id } = event.PileFlipped;
          // Update all cards in this pile to their final state
          const newVisuals = [...cardVisuals];
          // Iterate over current visuals to find cards in the flipped pile
          // This handles cases where cards are about to move to another pile (e.g. Waste -> Stock)
          cardVisuals.forEach((visual, id) => {
            if (visual.pile_id === pile_id) {
              if (finalCardVisuals[id]) {
                newVisuals[id] = {
                  ...visual,
                  face_up: finalCardVisuals[id].face_up,
                };
              }
            }
          });
          setCardVisuals(newVisuals);

          // Play pile flipped sound
          if (soundHooks) {
            soundHooks.pileFlipped();
          }

          await new Promise((r) =>
            setTimeout(r, 200 * GLOBAL_ANIMATION_FACTOR),
          );
        } else if ("PileShuffled" in event) {
          // Play shuffle sound
          if (soundHooks) {
            soundHooks.shuffle();
          }
          await new Promise((r) =>
            setTimeout(r, 100 * GLOBAL_ANIMATION_FACTOR),
          );
        } else if ("PileReversed" in event) {
          await new Promise((r) =>
            setTimeout(r, 100 * GLOBAL_ANIMATION_FACTOR),
          );
        }
      } else if (event === "Victory") {
        checkForVictory([event]);
      }
    }

    if (generation !== gameGeneration) return;

    setCardVisuals(finalCardVisuals);

    // Allow animations to settle
    // await new Promise((r) => setTimeout(r, 100 * GLOBAL_ANIMATION_FACTOR));

    if (generation === gameGeneration) {
      isAnimating = false;
      canUndo = game!.can_undo();
      saveGame();
    }
  }

  let cachedMode: any = null;

  let isDealing = false;

  async function startNewGame(
    forcedSeed: bigint | null = null,
    forceWinnable: boolean = false,
  ) {
    // Capture time immediately to ensure synchronized start regardless of loading delays
    const clickTime = Date.now();

    if (confettiInterval) {
      clearInterval(confettiInterval);
      confettiInterval = null;
    }
    try {
      confetti.reset();
    } catch (e) {
      // ignore
    }
    gameGeneration++;
    const myGen = gameGeneration;

    // Reset solution state
    solutionMoves = [];
    isSolving = false;

    // Cleanup animation
    isDealing = true;

    if (soundHooks) {
      soundHooks.dealing();
    }

    if (game && cardVisuals.length > 0) {
      isAnimating = true;

      // 1. Flip all cards face down
      setCardVisuals(cardVisuals.map((v) => ({ ...v, face_up: false })));
      await new Promise((r) => setTimeout(r, 150));
      if (myGen !== gameGeneration) {
        isDealing = false;
        return;
      }

      // 2. Move all cards to the stock pile (pile 0)
      const stockPile = pileVisuals.find((p) => p.pile_id === 0);
      if (stockPile) {
        setCardVisuals(
          cardVisuals.map((v) => ({
            ...v,
            position: stockPile.position,
            z_index: 0,
          })),
        );
        await new Promise((r) => setTimeout(r, 250));
      }
      if (myGen !== gameGeneration) {
        isDealing = false;
        return;
      }
    }

    if (!cachedMode || cachedMode.name.toLowerCase() !== gameMode) {
      const response = await fetch(
        `${import.meta.env.BASE_URL}modes/${gameMode}.json`,
      );
      cachedMode = await response.json();
    }
    const mode = cachedMode;

    if (myGen !== gameGeneration) {
      isDealing = false;
      return;
    }

    // Use forced seed if provided, otherwise quantize time to 10-second intervals
    let seed: bigint;
    if (forcedSeed !== null) {
      seed = forcedSeed;
    } else {
      let baseSeed = BigInt(Math.floor(clickTime / 10000));

      // Check for winning_deal param
      const params = new URLSearchParams(window.location.search);
      const modeDef = GAME_MODES.find((m) => m.id === gameMode);
      const isAlwaysWinnable = modeDef?.alwaysWinnable ?? false;

      if ((forceWinnable && !isAlwaysWinnable) || params.has("winning_deal")) {
        try {
          seed = find_winning_seed(baseSeed, mode);
        } catch (e) {
          console.warn("Could not find winning seed", e);
          seed = baseSeed;
        }
      } else {
        seed = baseSeed;
      }
    }
    currentSeed = seed;
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.set("deal", seed.toString());
      window.history.replaceState({}, "", url.toString());
    }
    game = new WasmGame(seed, mode);

    // Reset foundation tracking for new game
    lastFoundationCount = 0;
    isVictory = false;
    hasMoved = false;

    // Clear visuals before initialization to prevent accessing uninitialized piles
    setCardVisuals([]);
    pileVisuals = [];

    const initEvents = game.initialize();

    // Resize immediately after initialization so animations use correct dimensions
    handleResize();

    await processGameEvents(initEvents, new Set(), myGen);

    const dealEvents = game.deal();
    await processGameEvents(dealEvents, new Set(), myGen);

    hintInteraction = null;
    isDealing = false;
  }

  async function solveGame() {
    if (!game) return;
    isSolving = true;
    solverMetrics = null;
    try {
      // Yield to UI to show "Solving..." state
      await new Promise((r) => setTimeout(r, 10));
      const result = game.solve();
      if (result) {
        solutionMoves = result.solution;
        solverMetrics = result.metrics;
        console.log("Solution found:", solutionMoves.length, "moves");
      } else {
        console.log("No solution found within limit.");
        solutionMoves = [];
      }
    } catch (e) {
      console.error("Solver error:", e);
    } finally {
      isSolving = false;
    }
  }

  async function playNextSolutionMove() {
    if (solutionMoves.length === 0 || !game) return;
    const nextMove = solutionMoves.shift();
    solutionMoves = solutionMoves; // trigger reactivity

    try {
      const events = game.interact(nextMove);
      await processGameEvents(events);
    } catch (e) {
      console.error("Error playing solution move:", e);
    }
  }

  function saveGame() {
    if (!game) return;
    try {
      const state = game.save_game();
      localStorage.setItem("lolo_solitaire_state", state);
      if (currentSeed !== null) {
        localStorage.setItem("lolo_solitaire_seed", currentSeed.toString());
      }
    } catch (e) {
      console.error("Failed to save game", e);
    }
  }

  let boardArea = { x: 0, y: 0, width: 0, height: 0 };
  let lastFoundationCount: number | null = null;
  let hasMoved = false;
  let canUndo = false;

  function updateState() {
    if (!game) return;
    canUndo = game.can_undo();
    // Invert leftHanded because the engine default (false) is Stock Left,
    // but the user expects Left Handed Mode (true) to be Stock Left.
    const engineLeftHanded = !leftHanded;
    setCardVisuals(game.get_card_visuals(engineLeftHanded, boardArea.height));
    pileVisuals = game.get_pile_visuals(engineLeftHanded);

    const counts = new Map<number, number>();
    let currentFoundationCount = 0;

    // Identify foundation piles (Type 3 in Klondike)
    // TODO: This should not be hardcoded.
    const foundationPileIds = new Set(
      pileVisuals.filter((p) => p.pile_type === 3).map((p) => p.pile_id),
    );

    for (const card of cardVisuals) {
      counts.set(card.pile_id, (counts.get(card.pile_id) || 0) + 1);
      if (foundationPileIds.has(card.pile_id)) {
        currentFoundationCount++;
      }
    }
    pileCounts = counts;

    // Track foundation stats
    if (lastFoundationCount !== null) {
      const diff = currentFoundationCount - lastFoundationCount;
      if (diff !== 0) {
        lifetimeFoundationCards += diff;
        saveStats();
      }
    }
    lastFoundationCount = currentFoundationCount;

    if (import.meta.env.DEV) {
      try {
        const moves = game.get_all_valid_moves();
        validMovesCount = moves.length;
      } catch (e) {
        console.error("Failed to get valid moves", e);
      }
    }
  }

  function performLayout() {
    if (!game) return;
    const { width, height } = getStableViewport();

    const isMobile = width < 600;
    const isLandscape = width > height;
    const isLandscapeMobile = isLandscape && height < 600;

    let top = 0;
    let left = 0;
    let right = 0;
    let bottom = 0;

    if (isLandscapeMobile) {
      right = 80; // Side controls
      top = 10;
      bottom = 10;
    } else {
      top = isMobile ? 10 : 40; // Top bar
      bottom = isLandscapeMobile ? 10 : 120; // Bottom controls
    }

    boardArea = {
      x: left,
      y: top,
      width: width - left - right,
      height: height - top - bottom,
    };

    const result = game.resize(boardArea.width, boardArea.height);

    currentCardWidth = result.card_width;
    currentCardHeight = result.card_height;
    boardMarginLeft = result.margin_left;
    boardMarginTop = result.margin_top;

    totalBoardWidth = boardArea.width - 2 * result.margin_left;
  }

  function handleResize() {
    if (!game) return;

    isResizing = true;
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      isResizing = false;
    }, 100);

    performLayout();
    updateState();
  }

  function handlePointerDown(event: PointerEvent, cardId: number) {
    ensureAudioContext();

    if (event.button !== 0) return; // Only left button
    if (!game || isAnimating) return;
    const visual = cardVisuals[cardId];

    primaryDraggedCardId = cardId;
    initialMousePos = { x: event.clientX, y: event.clientY };
    dragOffset = { x: 0, y: 0 };
    isDragging = true;
    dragSourcePileId = null;
    draggedCardIds = new Set();

    if (visual.face_up) {
      try {
        const info = game.get_drag_info(cardId);
        dragSourcePileId = info.src_pile_id;

        // Identify all cards in the stack
        const stack = cardVisuals
          .map((v, i) => ({ ...v, id: i }))
          .filter(
            (v) =>
              v.pile_id === info.src_pile_id && v.z_index >= visual.z_index,
          );

        draggedCardIds = new Set(stack.map((c) => c.id));

        // Play pickup card sound with pitch based on number of cards
        if (soundHooks && draggedCardIds.size > 0) {
          soundHooks.pickupCard(draggedCardIds.size, true);
        }

        // Cache drop targets
        dropTargets = [];
        const piles = document.querySelectorAll(".pile");
        for (const pile of piles) {
          const rect = pile.getBoundingClientRect();
          const pileId = parseInt((pile as HTMLElement).dataset.pileId || "-1");
          if (pileId !== -1) {
            dropTargets.push({ id: pileId, rect, type: "pile", pileId });
          }
        }

        const cards = document.querySelectorAll(".card-wrapper");
        for (const card of cards) {
          const cId = parseInt((card as HTMLElement).dataset.cardId || "-1");
          if (draggedCardIds.has(cId)) continue;

          const rect = card.getBoundingClientRect();
          const visual = cardVisuals[cId];
          if (visual) {
            dropTargets.push({
              id: cId,
              rect,
              type: "card",
              pileId: visual.pile_id,
            });
          }
        }
      } catch (e) {
        // Not draggable
      }
    }

    try {
      const el = event.currentTarget as HTMLElement;
      el.setPointerCapture(event.pointerId);
      draggedCardInitialRect = el.getBoundingClientRect();
      event.preventDefault();
    } catch (e) {
      console.error("Failed to capture pointer", e);
    }
  }

  function handlePointerMove(event: PointerEvent) {
    if (!isDragging) return;
    event.preventDefault(); // Prevent scrolling on touch devices
    dragOffset = {
      x: event.clientX - initialMousePos.x,
      y: event.clientY - initialMousePos.y,
    };

    if (draggedCardInitialRect && dropTargets.length > 0) {
      const currentRect = new DOMRect(
        draggedCardInitialRect.x + dragOffset.x,
        draggedCardInitialRect.y + dragOffset.y,
        draggedCardInitialRect.width,
        draggedCardInitialRect.height,
      );

      let bestValidPileId: number | null = null;
      let maxValidArea = 0;

      for (const target of dropTargets) {
        const area = getIntersectionArea(currentRect, target.rect);
        if (area <= 0) continue;

        let isValid = false;
        if (game && dragSourcePileId !== null) {
          try {
            const validation = game.validate_move({
              DragCards: {
                src_pile_id: dragSourcePileId,
                dst_pile_id: target.pileId,
                number_of_cards: draggedCardIds.size,
              },
            });
            isValid = validation.is_valid;
          } catch (e) {
            // ignore
          }
        }

        if (isValid) {
          if (area > maxValidArea) {
            maxValidArea = area;
            bestValidPileId = target.pileId;
          }
        }
      }
      dragTargetPileId = bestValidPileId;
    }
  }

  function getIntersectionArea(r1: DOMRect, r2: DOMRect) {
    const xOverlap = Math.max(
      0,
      Math.min(r1.right, r2.right) - Math.max(r1.left, r2.left),
    );
    const yOverlap = Math.max(
      0,
      Math.min(r1.bottom, r2.bottom) - Math.max(r1.top, r2.top),
    );
    return xOverlap * yOverlap;
  }

  function handlePointerUp(event: PointerEvent) {
    if (!isDragging) return;

    const distance = Math.sqrt(dragOffset.x ** 2 + dragOffset.y ** 2);
    const wasClick = distance < 5;

    if (wasClick && primaryDraggedCardId !== null) {
      handleCardClick(primaryDraggedCardId);
    } else {
      const targetPileId = dragTargetPileId;

      if (targetPileId !== null && dragSourcePileId !== null) {
        try {
          lastInteractionTime = Date.now();
          const events = game!.interact({
            DragCards: {
              src_pile_id: dragSourcePileId,
              dst_pile_id: targetPileId,
              number_of_cards: draggedCardIds.size,
            },
          });
          hasMoved = true;
          processGameEvents(events, draggedCardIds);
          hintInteraction = null;

          // Play pickup sound if the move succeeded
          if (soundHooks) {
            soundHooks.pickupCard(draggedCardIds.size, true);
          }
        } catch (e) {
          console.error("Invalid move", e);
          // Play invalid drop sound
          if (soundHooks) {
            soundHooks.invalidDrop();
          }
        }
      } else if (draggedCardIds.size > 0) {
        // Play invalid drop sound if no valid target
        if (soundHooks) {
          soundHooks.invalidDrop();
        }
      }
    }

    // Boost Z-index for return flight (cancel drag)
    draggedCardIds.forEach((id) => {
      animatingCardIds.add(id);

      if (animationTimeouts.has(id)) {
        clearTimeout(animationTimeouts.get(id));
      }

      const timeoutId = setTimeout(() => {
        animatingCardIds.delete(id);
        animatingCardIds = animatingCardIds;
        animationTimeouts.delete(id);
      }, 400);

      animationTimeouts.set(id, timeoutId);
    });
    animatingCardIds = animatingCardIds;

    // Reset
    isDragging = false;
    draggedCardIds = new Set();
    dragOffset = { x: 0, y: 0 };
    dragSourcePileId = null;
    dragTargetPileId = null;
    dropTargets = [];
    draggedCardInitialRect = null;
    primaryDraggedCardId = null;
  }

  function handlePileClick(pileId: number, checkCooldown = true) {
    ensureAudioContext();
    if (isAnimating) return;
    if (checkCooldown) {
      const now = Date.now();
      if (now - lastInteractionTime < INTERACTION_COOLDOWN) return;
      lastInteractionTime = now;
    }
    try {
      const events = game!.interact({
        ClickPile: {
          pile_id: pileId,
        },
      });
      hasMoved = true;
      processGameEvents(events);
      hintInteraction = null;

      // Play stock clicked sound for pile 0 (stock pile in Klondike)
      if (soundHooks && pileId === 0) {
        soundHooks.stockClicked();
      }
    } catch (e) {
      console.error("Click failed", e);
    }
  }

  function handleUndo() {
    if (isAnimating) return;
    try {
      const events = game!.undo();
      processGameEvents(events);
      hintInteraction = null;

      // Play undo sound
      if (soundHooks) {
        soundHooks.undo();
      }

      // If we have a solution in progress, put the last move back onto the stack
      if (solutionMoves.length > 0 || isSolving) {
        // We need to re-solve or just invalidate the current solution if the user manually undid something
        // But if the user just wants to step back in the solution, we should ideally push the move back.
        // However, we don't easily know *which* move was just undone without tracking history separately.
        // A simple approach for "debug solver" is to just re-solve from the current state if needed,
        // OR, if we assume the user is only undoing the last "Next Move" click, we can try to recover.

        // Better approach for now: If the user undos, we clear the solution because we might have diverged
        // or simply stepped back. If they want to continue, they can hit "Solve" again which is fast.
        // BUT the user asked "doesn't desync the solver".

        // If we assume the user is stepping through the solution, undoing means we should be able to
        // just take the move we just undid and put it back at the start of `solutionMoves`.
        // But `game.undo()` doesn't return the move that was undone.

        // So, the most robust way to "not desync" is to re-calculate the solution from the current state.
        // Since the solver is deterministic and fast for solved states, this is acceptable for a dev tool.
        if (solutionMoves.length > 0) {
          // Option A: Clear it.
          // Option B: Re-solve immediately (might be slow if deep).
          // Option C: Do nothing and let the user click "Next Move" which might fail if the state doesn't match.

          // Let's go with Option A + Auto-Resolve if it was already solved.
          // Actually, if we just undid a move, the "next move" in the solution list is now wrong
          // (it points to the move AFTER the one we just undid).
          // So we MUST regenerate the solution or prepend the undone move.

          // Let's just clear it to be safe and avoid desync. The user can click "Solve" again.
          // Wait, the user specifically asked to NOT desync.
          // "if I press undo ... it doesn't desync".
          // This implies they want to step back and then step forward again.

          // To support this, we'd need to know what move was just undone.
          // Since we don't, let's trigger a re-solve in the background.
          solveGame();
        }
      }
    } catch (e) {
      console.error("Undo failed", e);
    }
  }

  function handleHint() {
    try {
      const hint = game!.get_hint();
      if (hint) {
        console.log("Hint:", hint);
        hintInteraction = hint;
      } else {
        console.log("No hint available");
        hintInteraction = null;
      }
    } catch (e) {
      console.error("Hint failed", e);
    }
  }

  function handleCardClick(cardId: number) {
    ensureAudioContext();
    if (!game || isAnimating) return;
    const now = Date.now();
    if (now - lastInteractionTime < INTERACTION_COOLDOWN) return;
    lastInteractionTime = now;

    try {
      const move = game.get_move_for_card(cardId);
      if (move) {
        const events = game.interact(move);
        hasMoved = true;
        processGameEvents(events);
        hintInteraction = null;

        // Play tap card sound with pitch shift based on card rank
        const visual = cardVisuals[cardId];
        if (soundHooks && visual) {
          soundHooks.tapCard(visual.rank, true);
        }
      } else {
        const visual = cardVisuals[cardId];
        if (!visual.face_up) {
          const info = game.get_drag_info(cardId);
          handlePileClick(info.src_pile_id, false);
        } else {
          // No move available for this card - play invalid tap sound
          if (soundHooks) {
            soundHooks.invalidTap();
          }
          shakeElement(`.scene[data-card-id="${cardId}"]`);
        }
      }
    } catch (e) {
      console.error("Card click failed", e);
      if (soundHooks) {
        soundHooks.invalidTap();
      }
      shakeElement(`.scene[data-card-id="${cardId}"]`);
    }
  }

  let hintSourceCardIds = new Set<number>();
  let hintDestCardIds = new Set<number>();

  $: {
    const sourceSet = new Set<number>();
    const destSet = new Set<number>();

    if (hintInteraction && cardVisuals.length > 0) {
      if (hintInteraction.DragCards) {
        const { src_pile_id, dst_pile_id, number_of_cards } =
          hintInteraction.DragCards;

        // Source cards
        const srcPileCards = cardVisuals
          .map((v, i) => ({ ...v, id: i }))
          .filter((v) => v.pile_id === src_pile_id)
          .sort((a, b) => a.z_index - b.z_index);

        const startIdx = Math.max(0, srcPileCards.length - number_of_cards);
        if (startIdx < srcPileCards.length) {
          sourceSet.add(srcPileCards[startIdx].id);
        }

        // Dest card (top of dest pile)
        const dstPileCards = cardVisuals
          .map((v, i) => ({ ...v, id: i }))
          .filter((v) => v.pile_id === dst_pile_id)
          .sort((a, b) => a.z_index - b.z_index);

        if (dstPileCards.length > 0) {
          destSet.add(dstPileCards[dstPileCards.length - 1].id);
        }
      } else if (hintInteraction.ClickPile) {
        const { pile_id } = hintInteraction.ClickPile;
        const pileCards = cardVisuals
          .map((v, i) => ({ ...v, id: i }))
          .filter((v) => v.pile_id === pile_id)
          .sort((a, b) => a.z_index - b.z_index);

        if (pileCards.length > 0) {
          sourceSet.add(pileCards[pileCards.length - 1].id);
        }
      }
    }
    hintSourceCardIds = sourceSet;
    hintDestCardIds = destSet;
  }

  let previousPileIds = new Map<number, number>();

  function setCardVisuals(newVisuals: typeof cardVisuals) {
    if (newVisuals.length === 0) {
      cardVisuals = [];
      previousPositions.clear();
      previousPileIds.clear();
      return;
    }

    // Detect position changes
    let addedAnimation = false;
    newVisuals.forEach((v, i) => {
      const key = `${v.position[0]},${v.position[1]}`;
      const prev = previousPositions.get(i);
      const prevPile = previousPileIds.get(i);

      // If we have a previous position and it's different
      if (prev && prev !== key) {
        if (!isResizing) {
          // Only boost z-index if the card is changing piles
          if (prevPile !== undefined && prevPile !== v.pile_id) {
            animatingCardIds.add(i);
            addedAnimation = true;

            if (animationTimeouts.has(i)) {
              clearTimeout(animationTimeouts.get(i));
            }

            const timeoutId = setTimeout(() => {
              animatingCardIds.delete(i);
              animatingCardIds = animatingCardIds;
              animationTimeouts.delete(i);
            }, 400);

            animationTimeouts.set(i, timeoutId);
          }
        }
      }
      previousPositions.set(i, key);
      previousPileIds.set(i, v.pile_id);
    });

    if (addedAnimation) {
      animatingCardIds = animatingCardIds;
    }
    cardVisuals = newVisuals;
  }

  $: hintSourcePileId =
    hintInteraction?.DragCards?.src_pile_id ??
    hintInteraction?.ClickPile?.pile_id;
  $: hintDestPileId = hintInteraction?.DragCards?.dst_pile_id;

  let showNewGameModal = false;
  let showModeSelection = false;

  function handleNewGameClick() {
    showNewGameModal = true;
  }

  function confirmNewGameAction() {
    startNewGame();
    showNewGameModal = false;
  }

  function cancelNewGameAction() {
    showNewGameModal = false;
    showModeSelection = false;
  }

  function shakeElement(selector: string) {
    const el = document.querySelector(selector);
    if (el) {
      el.classList.remove("shake");
      void (el as HTMLElement).offsetWidth; // Trigger reflow
      el.classList.add("shake");
    }
  }

  // Debug: Export/import game state via console
  if (typeof window !== "undefined") {
    (window as any).exportGameState = () => {
      if (game) {
        try {
          const state = game.save_game();
          console.log("GAME_STATE_EXPORT:", state);
          return state;
        } catch (e) {
          console.error("Failed to export game state", e);
        }
      } else {
        console.warn("No game instance to export");
      }
    };
    (window as any).importGameState = (json: string) => {
      try {
        const newGame = WasmGame.load_game(json);
        game = newGame;
        handleResize();
        updateState();
        console.log("GAME_STATE_IMPORT: Success");
      } catch (e) {
        console.error("Failed to import game state", e);
      }
    };
  }
</script>

<svelte:window
  on:pointermove={handlePointerMove}
  on:pointerup={handlePointerUp}
  on:resize={handleResize}
  on:keydown={(e) => {
    if (import.meta.env.DEV && (e.key === "v" || e.key === "V")) {
      checkForVictory(["Victory"]);
    }
  }}
/>

<main
  style="background-color: {BOARD_COLORS.find((c) => c.id === currentBoardColor)
    ?.value || '#2e8b57'}"
>
  <div class="controls">
    <button on:click={handleNewGameClick}>
      <span class="icon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          ><line x1="12" y1="5" x2="12" y2="19"></line><line
            x1="5"
            y1="12"
            x2="19"
            y2="12"
          ></line></svg
        >
      </span>
      <span class="label">New Game</span>
    </button>
    <button
      on:click={handleHint}
      disabled={isVictory || isDealing}
      class:disabled={isVictory || isDealing}
    >
      <span class="icon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          ><path d="M9 18h6" /><path d="M10 22h4" /><path
            d="M12 2a7 7 0 0 0-7 7c0 2.38 1.19 4.47 3 5.74V17a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2v-2.26C17.81 13.47 19 11.38 19 9a7 7 0 0 0-7-7z"
          /></svg
        >
      </span>
      <span class="label">Hint</span>
    </button>
    <button on:click={handleUndo} disabled={!canUndo} class:disabled={!canUndo}>
      <span class="icon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          ><path d="M9 14L4 9l5-5" /><path d="M20 20v-7a4 4 0 0 0-4-4H4" /></svg
        >
      </span>
      <span class="label">Undo</span>
    </button>
  </div>
  {#if game}
    <div
      class="game-board"
      style="
        --card-width: {currentCardWidth}px; 
        --card-height: {currentCardHeight}px;
        --base-card-width: {BASE_CARD_WIDTH}px;
        --base-card-height: {BASE_CARD_HEIGHT}px;
        --card-scale: {cardScale};
      "
    >
      <div
        class="board-content"
        style="transform: translate({boardArea.x +
          boardMarginLeft}px, {boardArea.y + boardMarginTop}px);"
      >
        <!-- Render Piles (Drop Zones) -->
        {#each pileVisuals as pile (pile.pile_id)}
          <div
            class="pile-wrapper"
            data-pile-id={pile.pile_id}
            style="position: absolute; left: {Math.round(
              pile.position[0],
            )}px; top: {Math.round(
              pile.position[1],
            )}px; width: var(--card-width); height: var(--card-height);"
          >
            <div
              class="pile"
              data-pile-id={pile.pile_id}
              class:foundation={pile.pile_type === 3}
              class:hint-dest={pile.pile_id === hintDestPileId &&
                hintDestCardIds.size === 0}
              class:hint-source={pile.pile_id === hintSourcePileId &&
                hintSourceCardIds.size === 0}
              class:drag-target={dragTargetPileId === pile.pile_id &&
                !effectivePileHasCards.has(pile.pile_id)}
              style="width: 100%; height: 100%;"
              on:click={() => handlePileClick(pile.pile_id)}
              role="button"
              tabindex="0"
              on:keydown={() => {}}
            >
              <div class="empty-slot">
                {#if pile.pile_type === 0 && (pileCounts.get(pile.pile_id) || 0) === 0}
                  <div class="reload-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="40%"
                      height="40%"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="rgba(255,255,255,0.5)"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      ><path d="M23 4v6h-6" /><path d="M1 20v-6h6" /><path
                        d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"
                      /></svg
                    >
                  </div>
                {/if}
              </div>
            </div>
          </div>
        {/each}

        <!-- Render Cards -->
        {#each cardVisuals as visual, cardId}
          {#if visual && visual.rank > 0}
            <div
              class="card-wrapper"
              data-card-id={cardId}
              style="
                z-index: {visual.z_index +
                (draggedCardIds.has(cardId) ? 5000 : 0) +
                (animatingCardIds.has(cardId) ? 2000 : 0)};
                transform: translate({visual.position[0] +
                (draggedCardIds.has(cardId) ? dragOffset.x : 0)}px, {visual
                .position[1] +
                (draggedCardIds.has(cardId) ? dragOffset.y : 0)}px);
                transition: {draggedCardIds.has(cardId) || isResizing
                ? 'none'
                : 'transform 0.3s ease-out'};
              "
              class:hint-source={hintSourceCardIds.has(cardId)}
              class:hint-dest={hintDestCardIds.has(cardId)}
              class:drag-target={dragTargetPileId === visual.pile_id &&
                effectiveTopCardIds.has(cardId)}
              on:pointerdown={(e) => handlePointerDown(e, cardId)}
              role="listitem"
            >
              <Card
                id={cardId}
                rank={visual.rank}
                suit={visual.suit}
                faceUp={visual.face_up}
                backVariant={currentCardBackId}
                {highContrastMode}
                isPickedUp={draggedCardIds.has(cardId)}
                on:activate={() => handleCardClick(cardId)}
              />
            </div>
          {/if}
        {/each}
      </div>
    </div>
  {/if}

  {#if showSettings || (showUnlockReveal && pendingUnlock)}
    <div class="shared-backdrop" transition:fade={{ duration: 300 }}></div>
  {/if}

  {#if showUnlockReveal && pendingUnlock}
    <UnlockReveal
      unlockedId={pendingUnlock}
      targetRect={unlockTargetRect}
      dealSeed={currentSeed?.toString()}
      isDuplicate={isDuplicateUnlock}
      {canReroll}
      on:reroll={handleReroll}
      on:dismiss={async () => {
        showSettings = true;
        // Wait for SettingsMenu to mount and scroll
        await new Promise((r) => setTimeout(r, 100));

        // Find the target element in the settings grid
        // We look for the element that matches the pending unlock ID
        // The SettingsMenu renders items with data-id attributes or we can find by class
        // Assuming SettingsMenu renders items that we can query.
        // We might need to update SettingsMenu to ensure items have data-id.
        // Based on SettingsMenu code, it scrolls to .grid-item.selected or .new-unlock

        const targetEl = document.querySelector(`[data-id="${pendingUnlock}"]`);
        if (targetEl) {
          unlockTargetRect = targetEl.getBoundingClientRect();
        } else {
          // Fallback if not found (e.g. different tab)
          // Just close immediately
          showUnlockReveal = false;
          pendingUnlock = null;
        }
      }}
      on:close={() => {
        showUnlockReveal = false;
        unlockTargetRect = null;
        pendingUnlock = null; // Clear pending status after animation completes
      }}
    />
  {/if}

  {#if showSettings}
    <SettingsMenu
      bind:cardFace
      bind:cardBack={currentCardBackId}
      bind:boardColor={currentBoardColor}
      bind:soundEnabled
      bind:leftHanded
      bind:gameMode
      {soundHooks}
      {unlockedItems}
      {pendingUnlock}
      on:close={() => {
        showSettings = false;
        if (pendingUnlock) pendingUnlock = null;
      }}
      on:change={(e) => {
        // Handle other changes if any
      }}
    />
  {/if}

  {#if showNewGameModal}
    {@const currentModeDef = GAME_MODES.find((m) => m.id === gameMode)}
    <div
      class="modal-overlay"
      on:click={cancelNewGameAction}
      role="presentation"
      transition:fade={{ duration: 200 }}
    >
      <div
        class="modal"
        on:click|stopPropagation
        role="dialog"
        aria-modal="true"
        tabindex="-1"
        on:keydown={() => {}}
        in:scale={{ duration: 400, start: 0.95 }}
      >
        {#if showModeSelection}
          <div
            class="header"
            style="display: flex; align-items: center; gap: 12px; margin-bottom: 20px;"
          >
            <button
              class="back-btn"
              style="background: none; border: none; color: white; font-size: 24px; cursor: pointer; padding: 0;"
              on:click={() => (showModeSelection = false)}
            >
              ←
            </button>
            <h2 style="margin: 0;">Select Mode</h2>
          </div>
          <div
            class="modes-list"
            style="display: flex; flex-direction: column; gap: 8px; max-height: 60vh; overflow-y: auto; padding-right: 4px;"
          >
            {#each GAME_MODES as mode}
              <button
                class="mode-row"
                class:selected={gameMode === mode.id}
                style="background: #252525; border: 2px solid {gameMode ===
                mode.id
                  ? '#4a9eff'
                  : '#333'}; border-radius: 12px; padding: 12px; cursor: pointer; display: flex; align-items: center; gap: 16px; transition: all 0.2s; color: white; text-align: left; width: 100%;"
                on:click={() => {
                  gameMode = mode.id;
                  localStorage.setItem("lolo_solitaire_game_mode", mode.id);
                  showModeSelection = false;
                }}
              >
                <div
                  class="mode-icon"
                  style="width: 40px; height: 40px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-weight: 900; font-size: 1rem; color: white; flex-shrink: 0; background: {mode.color}"
                >
                  {mode.icon}
                </div>
                <div
                  class="mode-info"
                  style="flex: 1; display: flex; flex-direction: column; gap: 2px;"
                >
                  <div
                    class="mode-name"
                    style="font-weight: bold; font-size: 1rem;"
                  >
                    {mode.name}
                  </div>
                  <div
                    class="mode-desc"
                    style="font-size: 0.8rem; color: #aaa; line-height: 1.2;"
                  >
                    {mode.description}
                  </div>
                </div>
                {#if gameMode === mode.id}
                  <div class="selected-check" style="color: #4a9eff;">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="3"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      ><polyline points="20 6 9 17 4 12" /></svg
                    >
                  </div>
                {/if}
              </button>
            {/each}
          </div>
        {:else}
          <h2>New Game</h2>

          <div class="mode-selector">
            <div class="mode-selector-label">Game Mode</div>
            <button
              class="mode-select-btn"
              on:click={() => {
                showModeSelection = true;
              }}
            >
              <div
                class="mode-select-icon"
                style="background: {currentModeDef?.color || '#4a9eff'}"
              >
                {currentModeDef?.icon || "K"}
              </div>
              <div class="mode-select-info">
                <div class="mode-select-name">
                  {currentModeDef?.name || "Klondike"}
                </div>
              </div>
              <span class="chevron">›</span>
            </button>
          </div>

          <div class="modal-menu">
            {#if currentModeDef?.alwaysWinnable}
              <button
                class="modal-btn"
                on:click={() => {
                  startNewGame(null, false);
                  showNewGameModal = false;
                }}
              >
                <span class="icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    ><polyline points="16 3 21 3 21 8"></polyline><line
                      x1="4"
                      y1="20"
                      x2="21"
                      y2="3"
                    ></line><polyline points="21 16 21 21 16 21"
                    ></polyline><line x1="15" y1="15" x2="21" y2="21"
                    ></line><line x1="4" y1="4" x2="9" y2="9"></line></svg
                  >
                </span>
                <span class="label">New Game</span>
              </button>
            {:else if currentModeDef?.solvable}
              <button
                class="modal-btn"
                on:click={() => {
                  startNewGame(null, false);
                  showNewGameModal = false;
                }}
              >
                <span class="icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    ><polyline points="16 3 21 3 21 8"></polyline><line
                      x1="4"
                      y1="20"
                      x2="21"
                      y2="3"
                    ></line><polyline points="21 16 21 21 16 21"
                    ></polyline><line x1="15" y1="15" x2="21" y2="21"
                    ></line><line x1="4" y1="4" x2="9" y2="9"></line></svg
                  >
                </span>
                <span class="label">Random Shuffle</span>
              </button>
              <button
                class="modal-btn"
                on:click={() => {
                  startNewGame(null, true);
                  showNewGameModal = false;
                }}
              >
                <span class="icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    ><polygon
                      points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
                    ></polygon></svg
                  >
                </span>
                <span class="label">Winnable Shuffle</span>
              </button>
            {:else}
              <button
                class="modal-btn"
                on:click={() => {
                  startNewGame(null, false);
                  showNewGameModal = false;
                }}
              >
                <span class="icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    ><polyline points="16 3 21 3 21 8"></polyline><line
                      x1="4"
                      y1="20"
                      x2="21"
                      y2="3"
                    ></line><polyline points="21 16 21 21 16 21"
                    ></polyline><line x1="15" y1="15" x2="21" y2="21"
                    ></line><line x1="4" y1="4" x2="9" y2="9"></line></svg
                  >
                </span>
                <span class="label">Random Shuffle</span>
              </button>
            {/if}
            <button
              class="modal-btn restart"
              on:click={() => {
                startNewGame(currentSeed, false);
                showNewGameModal = false;
              }}
            >
              <span class="icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  ><path d="M23 4v6h-6"></path><path
                    d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"
                  ></path></svg
                >
              </span>
              <span class="label">Restart</span>
            </button>

            <button class="modal-btn cancel" on:click={cancelNewGameAction}>
              <span class="icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  ><line x1="18" y1="6" x2="6" y2="18"></line><line
                    x1="6"
                    y1="6"
                    x2="18"
                    y2="18"
                  ></line></svg
                >
              </span>
              <span class="label">Cancel</span>
            </button>
          </div>
        {/if}
      </div>
    </div>
  {/if}

  {#if showReplaceGameModal}
    <div
      class="modal-overlay"
      on:click={() => {
        showReplaceGameModal = false;
        // If cancelled, we should probably update the URL to match the current game
        if (currentSeed !== null && typeof window !== "undefined") {
          const url = new URL(window.location.href);
          url.searchParams.set("deal", currentSeed.toString());
          window.history.replaceState({}, "", url.toString());
        }
      }}
      role="presentation"
    >
      <div
        class="modal"
        on:click|stopPropagation
        role="dialog"
        aria-modal="true"
        tabindex="-1"
        on:keydown={() => {}}
      >
        <h2>Replace current game?</h2>
        <div class="modal-buttons">
          <button
            class="modal-btn cancel"
            on:click={() => {
              showReplaceGameModal = false;
              if (currentSeed !== null && typeof window !== "undefined") {
                const url = new URL(window.location.href);
                url.searchParams.set("deal", currentSeed.toString());
                window.history.replaceState({}, "", url.toString());
              }
            }}>Cancel</button
          >
          <button
            class="modal-btn confirm"
            on:click={() => {
              showReplaceGameModal = false;
              if (urlSeed !== null) {
                startNewGame(urlSeed);
              }
            }}>Replace</button
          >
        </div>
      </div>
    </div>
  {/if}

  {#if import.meta.env.DEV}
    <div
      style="position: fixed; bottom: 80px; right: 20px; z-index: 9999; background: rgba(0,0,0,0.5); padding: 10px; border-radius: 5px; color: white; display: flex; flex-direction: column; gap: 5px;"
    >
      <button
        on:click={solveGame}
        disabled={isSolving}
        style="padding: 5px; cursor: pointer;"
      >
        {isSolving ? "Solving..." : "Solve"}
      </button>
      <button
        on:click={playNextSolutionMove}
        disabled={solutionMoves.length === 0}
        style="padding: 5px; cursor: pointer;"
      >
        Next Move ({solutionMoves.length})
      </button>
      <div style="font-size: 12px;">Valid Moves: {validMovesCount}</div>
      {#if solverMetrics}
        <div
          style="font-size: 10px; margin-top: 5px; border-top: 1px solid rgba(255,255,255,0.3); padding-top: 5px;"
        >
          <div>Length: {solverMetrics.game_length}</div>
          <div>Undos: {solverMetrics.undos_used}</div>
          <div>Avg Opts: {solverMetrics.avg_options_per_move.toFixed(2)}</div>
          <div>Forced: {solverMetrics.forced_moves_count}</div>
        </div>
      {/if}
    </div>
  {/if}

  <div class="settings-control">
    {#if pendingUnlock}
      <GiftButton
        on:click={() => {
          showUnlockReveal = true;
        }}
      />
    {/if}
    <button on:click={() => (showSettings = true)} aria-label="Settings">
      <span class="icon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          ><circle cx="12" cy="12" r="3" /><path
            d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"
          /></svg
        >
      </span>
    </button>
  </div>
</main>

<style>
  .controls {
    position: absolute;
    bottom: 20px;
    left: 20px;
    z-index: 1000;
    display: flex;
    gap: 16px;
  }

  .controls button {
    display: flex;
    align-items: center;
    padding: 0;
    color: white;
    border: 1px solid transparent;
    border-radius: 12px;
    cursor: pointer;
    font-weight: 500;
    font-size: 16px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
    transition:
      transform 0.1s,
      opacity 0.35s cubic-bezier(0.4, 0, 0.2, 1),
      filter 0.35s cubic-bezier(0.4, 0, 0.2, 1),
      background 0.35s cubic-bezier(0.4, 0, 0.2, 1);
    overflow: hidden;
    background:
      linear-gradient(#5c6bc0, #5c6bc0) padding-box,
      linear-gradient(to bottom, rgba(255, 255, 255, 0.4), rgba(0, 0, 0, 0.2))
        border-box;
  }

  .controls button:not(.disabled):hover {
    background:
      linear-gradient(#4f5b9e, #4f5b9e) padding-box,
      linear-gradient(to bottom, rgba(255, 255, 255, 0.4), rgba(0, 0, 0, 0.2))
        border-box;
  }

  .controls button:not(.disabled):active {
    transform: translateY(2px);
    box-shadow: 0 2px 3px rgba(0, 0, 0, 0.2);
  }

  .controls button.disabled {
    opacity: 0.5;
    cursor: not-allowed;
    filter: grayscale(0.5) brightness(0.8);
    /* Optionally, you can add a slight blur for extra effect: */
    /* filter: grayscale(0.5) brightness(0.8) blur(0.5px); */
  }

  .icon {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px 12px;
    background-color: rgba(0, 0, 0, 0.1);
    border-right: 1px solid rgba(255, 255, 255, 0.1);
  }

  .icon:last-child {
    border-right: none;
  }

  .label {
    padding: 10px 16px;
  }

  .game-board {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  main {
    position: relative;
    width: 100%;
    height: 100dvh;
    overflow: hidden;
    transition: background-color 3s ease;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.5' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.1'/%3E%3C/svg%3E");
  }

  .pile {
    position: absolute;
    width: var(--card-width);
    height: var(--card-height);
    border: 2px dashed rgba(255, 255, 255, 0.3);
    border-radius: calc(8px * var(--card-scale, 1));
    box-sizing: border-box;
  }

  .pile.foundation {
    border: 2px solid rgba(255, 255, 255, 0.5);
    background-color: rgba(255, 255, 255, 0.1);
  }

  .card-wrapper {
    position: absolute;
    width: var(--card-width);
    height: var(--card-height);
    box-sizing: border-box;
    touch-action: none;
    user-select: none;
    -webkit-user-select: none;
    will-change: transform, left, top;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
    pointer-events: none;
    touch-action: none;
  }

  .pile::before,
  .card-wrapper::before {
    content: "";
    position: absolute;
    top: -4px;
    left: -4px;
    right: -4px;
    bottom: -4px;
    border-radius: calc(12px * var(--card-scale, 1));
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
    z-index: 5;
  }

  .hint-source::before {
    box-shadow: 0 0 15px 4px rgba(255, 165, 0, 0.6);
    border: 4px solid rgb(255, 200, 97);
    opacity: 1;
  }

  .hint-dest::before {
    box-shadow: 0 0 15px 4px rgba(0, 120, 255, 0.6);
    border: 4px solid rgba(0, 120, 255, 0.9);
    opacity: 1;
  }

  .pile::after,
  .card-wrapper::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: calc(8px * var(--card-scale, 1));
    background-color: rgba(109, 87, 255, 0.2);
    opacity: 0;
    transition: opacity 1s ease;
    pointer-events: none;
    z-index: 10;
  }

  .drag-target::after {
    opacity: 1;
  }

  .empty-slot {
    width: 100%;
    height: 100%;
  }

  .reload-icon {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  @media (max-width: 600px) {
    .controls .label {
      display: none;
    }
    .modal {
      width: 90%;
      min-width: auto;
      max-width: 320px;
      padding: 16px;
    }
    .modal-menu {
      min-width: auto;
      width: 100%;
    }
  }

  @media (max-height: 600px) and (orientation: landscape) {
    .controls {
      left: auto;
      right: 20px;
      top: 20px;
      bottom: auto;
      flex-direction: column;
    }

    .controls .label {
      display: none;
    }

    .settings-control {
      flex-direction: column;
    }
  }

  .settings-control {
    position: absolute;
    bottom: 20px;
    right: 20px;
    z-index: 1000;
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .settings-control button {
    display: flex;
    align-items: center;
    padding: 0;
    color: white;
    border: 1px solid transparent;
    border-radius: 50%;
    cursor: pointer;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
    transition: transform 0.1s;
    overflow: hidden;
    background:
      linear-gradient(#5c6bc0, #5c6bc0) padding-box,
      linear-gradient(to bottom, rgba(255, 255, 255, 0.4), rgba(0, 0, 0, 0.2))
        border-box;
    width: 48px;
    height: 48px;
    justify-content: center;
  }

  .settings-control button:hover {
    background:
      linear-gradient(#4f5b9e, #4f5b9e) padding-box,
      linear-gradient(to bottom, rgba(255, 255, 255, 0.4), rgba(0, 0, 0, 0.2))
        border-box;
  }

  .settings-control button:active {
    transform: translateY(2px);
    box-shadow: 0 2px 3px rgba(0, 0, 0, 0.2);
  }

  .settings-control .icon {
    border: none;
    background: none;
    padding: 0;
  }

  @keyframes shake {
    0%,
    100% {
      transform: translateX(0) scale(var(--card-scale, 1));
    }
    25% {
      transform: translateX(-5px) scale(var(--card-scale, 1));
    }
    75% {
      transform: translateX(5px) scale(var(--card-scale, 1));
    }
  }

  :global(.shake) {
    animation: shake 0.2s ease-in-out 2;
  }

  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
    backdrop-filter: blur(2px);
  }

  .modal {
    background: #2a2a2a;
    padding: 24px;
    border-radius: 12px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
    text-align: center;
    color: white;
    min-width: 280px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    box-sizing: border-box;
  }

  .modal h2 {
    margin: 0 0 24px 0;
    font-size: 1.25rem;
  }

  .modal-buttons {
    display: flex;
    gap: 12px;
    justify-content: center;
  }

  .modal-buttons .modal-btn {
    width: auto;
    flex: 1;
    justify-content: center;
    text-align: center;
    padding: 12px 24px;
  }

  .modal-menu {
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-width: 240px;
  }

  .modal-btn {
    display: flex;
    align-items: center;
    padding: 0;
    color: white;
    border: 1px solid transparent;
    border-radius: 12px;
    cursor: pointer;
    font-weight: 500;
    font-size: 16px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
    transition:
      transform 0.1s,
      opacity 0.35s cubic-bezier(0.4, 0, 0.2, 1),
      filter 0.35s cubic-bezier(0.4, 0, 0.2, 1),
      background 0.35s cubic-bezier(0.4, 0, 0.2, 1);
    overflow: hidden;
    background:
      linear-gradient(#5c6bc0, #5c6bc0) padding-box,
      linear-gradient(to bottom, rgba(255, 255, 255, 0.4), rgba(0, 0, 0, 0.2))
        border-box;
    width: 100%;
    text-align: left;
  }

  .modal-btn:hover {
    background:
      linear-gradient(#4f5b9e, #4f5b9e) padding-box,
      linear-gradient(to bottom, rgba(255, 255, 255, 0.4), rgba(0, 0, 0, 0.2))
        border-box;
  }

  .modal-btn:active {
    transform: translateY(2px);
    box-shadow: 0 2px 3px rgba(0, 0, 0, 0.2);
  }

  .modal-btn.cancel {
    background:
      linear-gradient(#7f8c8d, #7f8c8d) padding-box,
      linear-gradient(to bottom, rgba(255, 255, 255, 0.4), rgba(0, 0, 0, 0.2))
        border-box;
  }

  .modal-btn.cancel:hover {
    background:
      linear-gradient(#95a5a6, #95a5a6) padding-box,
      linear-gradient(to bottom, rgba(255, 255, 255, 0.4), rgba(0, 0, 0, 0.2))
        border-box;
  }

  .modal-btn.restart {
    background:
      linear-gradient(#546e7a, #546e7a) padding-box,
      linear-gradient(to bottom, rgba(255, 255, 255, 0.4), rgba(0, 0, 0, 0.2))
        border-box;
  }

  .modal-btn.restart:hover {
    background:
      linear-gradient(#455a64, #455a64) padding-box,
      linear-gradient(to bottom, rgba(255, 255, 255, 0.4), rgba(0, 0, 0, 0.2))
        border-box;
  }

  .mode-selector {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 20px;
    padding-bottom: 20px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  .mode-selector-label {
    font-size: 0.9rem;
    color: #aaa;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .mode-select-btn {
    display: flex;
    align-items: center;
    gap: 12px;
    background: rgba(255, 255, 255, 0.05);
    border: 2px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    padding: 10px 16px;
    color: white;
    cursor: pointer;
    transition: all 0.2s;
    text-align: left;
  }

  .mode-row:hover {
    background: #333 !important;
    transform: translateX(4px);
  }

  .mode-row.selected {
    border-color: #4a9eff !important;
    background: #2a3a4a !important;
  }

  .mode-icon {
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  }

  .mode-select-btn:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.2);
  }

  .mode-select-icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 0.9rem;
    flex-shrink: 0;
  }

  .mode-select-info {
    flex: 1;
  }

  .mode-select-name {
    font-weight: bold;
    font-size: 1rem;
  }

  .back-btn:hover {
    background: rgba(255, 255, 255, 0.1) !important;
    border-radius: 50%;
  }

  .shared-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
    z-index: 9000;
  }
</style>
